import { matchPath, useLocation, useNavigate } from "react-router-dom"
import { BottomNavigation, BottomNavigationAction, Paper } from "@mui/material"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { makeStyles } from "tss-react/mui"

import { LocalizedStrict } from "../../modules/localization/components/AppLocalized"
import { whenPrintingPageOnly } from "../components/print/printPageOnly"
import { SafeArea } from "../components/skeleton/SafeArea"

import { NarrowNavItemsProps, useVisibleNavItems } from "./NarrowNavItems"

const NARROW_NAV_BOTTOM_BAR_HEIGHT = "3.5rem"

const useStyles = makeStyles()((theme) => ({
    // sticky rather than fixed: at the end of the page it gives way to the footer below the layout
    root: {
        position: "sticky",
        bottom: 0,
        zIndex: theme.zIndex.appBar,
        borderTop: `1px solid ${theme.palette.tailwind.slate[200]}`,
        ...whenPrintingPageOnly({ display: "none" }),
    },
    navigation: {
        height: NARROW_NAV_BOTTOM_BAR_HEIGHT,
    },
    action: {
        minWidth: 0,
        color: theme.palette.tailwind.slate[500],
        "&.Mui-selected": {
            color: theme.palette.tailwind.blue[600],
        },
        "& .MuiBottomNavigationAction-label": {
            maxWidth: "100%",
            overflow: "hidden",
            whiteSpace: "nowrap",
            textOverflow: "ellipsis",
        },
    },
    icon: {
        fontSize: "1.125rem",
        marginBottom: ".25rem",
    },
}))

export function NarrowNavBottomBar({ navItems }: NarrowNavItemsProps) {
    const { classes } = useStyles()
    const navigate = useNavigate()
    const { pathname } = useLocation()

    const visibleItems = useVisibleNavItems(navItems)
    const activeRoute = visibleItems.find((item) => matchPath(item.route, pathname))?.route ?? false

    return (
        <Paper square elevation={0} className={classes.root}>
            <SafeArea top={false} left={false} right={false} fullHeight={false}>
                <BottomNavigation
                    showLabels
                    value={activeRoute}
                    onChange={(_, route: string) => navigate(route)}
                    className={classes.navigation}
                >
                    {visibleItems.map((item) => (
                        <BottomNavigationAction
                            key={item.route}
                            value={item.route}
                            className={classes.action}
                            icon={<FontAwesomeIcon icon={item.icon} className={classes.icon} />}
                            label={
                                <LocalizedStrict id={item.shortLabelKey}>
                                    <span>{item.label}</span>
                                </LocalizedStrict>
                            }
                        />
                    ))}
                </BottomNavigation>
            </SafeArea>
        </Paper>
    )
}
